import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Firestore, collection, collectionData, doc, updateDoc, query, where, orderBy, limit, Timestamp } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, map, of, switchMap, catchError, BehaviorSubject, Subscription } from 'rxjs';
import { Review, ReviewDocument, ReviewSyncStatus, reviewDocumentToReview } from '../models/review.model';
import { readCache, writeCache } from './cache.utils';

@Injectable({
  providedIn: 'root'
})
export class ReviewService implements OnDestroy {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  
  // Place ID for The Soul Simmer
  private readonly PLACE_ID = 'ChIJC3k3mrxbUjoRjTZPEsj9lA4';
  
  // Signals for reactive state
  private reviewsSignal = signal<Review[]>([]);
  private loadingSignal = signal<boolean>(false);
  private syncStatusSignal = signal<ReviewSyncStatus>({
    lastSyncedAt: null,
    totalReviews: 0,
    featuredReviews: 0
  });
  
  // Computed signals
  readonly reviews = this.reviewsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly syncStatus = this.syncStatusSignal.asReadonly();
  
  // Computed: Featured reviews for homepage
  readonly featuredReviews = computed(() => {
    return this.reviewsSignal()
      .filter(r => r.isAdminFeatured)
      .sort((a, b) => {
        const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return timeB - timeA;
      })
      .slice(0, 3);
  });
  
  // Computed: Reviews by rating
  readonly reviewsByRating = computed(() => {
    const reviews = this.reviewsSignal();
    return {
      five: reviews.filter(r => r.rating === 5),
      four: reviews.filter(r => r.rating === 4),
      three: reviews.filter(r => r.rating === 3),
      two: reviews.filter(r => r.rating === 2),
      one: reviews.filter(r => r.rating === 1)
    };
  });

  private static readonly CACHE_KEY = 'reviews_cache';
  private static readonly CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

  /** Active Firestore/RxJS subscription; reused across home navigations to avoid the prior leak. */
  private reviewsSub?: Subscription;
  private reviewsLoaded = false;

  constructor() {
    // Hydrate reviews synchronously from localStorage so the testimonials
    // section renders instantly on cold/PWA opens, before the stream reconnects.
    const cached = readCache<Review[]>(ReviewService.CACHE_KEY, ReviewService.CACHE_MAX_AGE_MS);
    if (cached && cached.length > 0) {
      this.reviewsSignal.set(cached);
      this.updateSyncStatus(cached);
      this.reviewsLoaded = true;
      console.log(`[ReviewService] Hydrated ${cached.length} reviews from cache instantly.`);
    }
  }

  /**
   * Load all reviews from Firestore. Idempotent — repeated calls (e.g. every home
   * navigation) reuse the existing subscription and cached signal instead of
   * opening a new Firestore stream each time (the previous implementation leaked
   * one stream per visit). Pass forceReload=true to re-subscribe (used by
   * syncGoogleReviews and admin review-management actions).
   */
  loadReviews(forceReload = false): void {
    if (!forceReload && (this.reviewsSub && !this.reviewsSub.closed)) {
      return; // Already listening — reuse the existing stream + cached signal.
    }
    if (!forceReload && this.reviewsLoaded) {
      return; // Already have data; skip the network round-trip entirely.
    }

    this.loadingSignal.set(true);

    // Reuse any prior slot: unsubscribe before re-subscribing prevents stacking.
    this.reviewsSub?.unsubscribe();

    const reviewsCollection = collection(this.firestore, 'reviews');
    // Limit the query — the home testimonials + dashboard only need recent reviews.
    const reviewsQuery = query(reviewsCollection, orderBy('createdAt', 'desc'), limit(20));

    this.reviewsSub = collectionData(reviewsQuery, { idField: 'id' }).pipe(
      map(docs => docs.map(doc => reviewDocumentToReview(doc as ReviewDocument))),
      catchError(error => {
        console.error('Error loading reviews:', error);
        return of([]);
      })
    ).subscribe({
      next: (reviews) => {
        this.reviewsSignal.set(reviews);
        this.updateSyncStatus(reviews);
        this.reviewsLoaded = true;
        this.loadingSignal.set(false);
        // Persist to localStorage for instant hydration on next cold/PWA open.
        writeCache(ReviewService.CACHE_KEY, reviews);
      },
      error: (error) => {
        console.error('Error in reviews subscription:', error);
        this.loadingSignal.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.reviewsSub?.unsubscribe();
  }

  /**
   * Get featured reviews for homepage display
   * Returns Observable for async pipe usage
   * Note: Fetches all featured reviews and sorts client-side to avoid composite index requirement
   */
  getFeaturedReviews(): Observable<Review[]> {
    const reviewsCollection = collection(this.firestore, 'reviews');
    const featuredQuery = query(
      reviewsCollection,
      where('isAdminFeatured', '==', true),
      limit(10)
    );
    
    return collectionData(featuredQuery, { idField: 'id' }).pipe(
      map(docs => {
        const reviews = docs.map(doc => reviewDocumentToReview(doc as ReviewDocument));
        // Sort client-side by createdAt descending, then take top 3
        return reviews.sort((a, b) => {
          const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
          const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
          return timeB - timeA;
        }).slice(0, 3);
      }),
      catchError(error => {
        console.error('Error fetching featured reviews:', error);
        return of([]);
      })
    );
  }

  /**
   * Get all reviews (for admin panel)
   */
  getAllReviews(): Observable<Review[]> {
    const reviewsCollection = collection(this.firestore, 'reviews');
    const reviewsQuery = query(reviewsCollection, orderBy('createdAt', 'desc'));
    
    return collectionData(reviewsQuery, { idField: 'id' }).pipe(
      map(docs => docs.map(doc => reviewDocumentToReview(doc as ReviewDocument))),
      catchError(error => {
        console.error('Error fetching all reviews:', error);
        return of([]);
      })
    );
  }

  /**
   * Toggle featured status of a review
   */
  async toggleFeatured(reviewId: string, featured: boolean): Promise<void> {
    const reviewRef = doc(this.firestore, 'reviews', reviewId);
    
    await updateDoc(reviewRef, {
      isAdminFeatured: featured
    });
    
    // Update local signal
    this.reviewsSignal.update(reviews => 
      reviews.map(r => r.id === reviewId ? { ...r, isAdminFeatured: featured } : r)
    );
  }

  /**
   * Sync reviews from Google Places API
   * Calls Cloud Function to fetch and store reviews
   */
  async syncGoogleReviews(): Promise<{ success: boolean; message: string; count?: number }> {
    this.loadingSignal.set(true);
    
    try {
      const syncFn = httpsCallable(this.functions, 'fetchGoogleReviews');
      const result = await syncFn({ placeId: this.PLACE_ID });
      const data = result.data as any;
      
      if (data.success) {
        // Reload reviews after sync — force a fresh subscription since cached data is now stale.
        this.loadReviews(true);
        return {
          success: true,
          message: data.message || 'Reviews synced successfully',
          count: data.count
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to sync reviews'
        };
      }
    } catch (error: any) {
      console.error('Error syncing Google reviews:', error);
      return {
        success: false,
        message: error.message || 'Failed to sync reviews'
      };
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update sync status based on current reviews
   */
  private updateSyncStatus(reviews: Review[]): void {
    const featured = reviews.filter(r => r.isAdminFeatured).length;
    const latestReview = reviews[0];
    
    this.syncStatusSignal.set({
      lastSyncedAt: latestReview?.createdAt || null,
      totalReviews: reviews.length,
      featuredReviews: featured
    });
  }

  /**
   * Get review count by rating
   */
  getRatingDistribution(): { rating: number; count: number; percentage: number }[] {
    const reviews = this.reviewsSignal();
    const total = reviews.length;
    
    if (total === 0) {
      return [5, 4, 3, 2, 1].map(r => ({ rating: r, count: 0, percentage: 0 }));
    }
    
    return [5, 4, 3, 2, 1].map(rating => {
      const count = reviews.filter(r => r.rating === rating).length;
      return {
        rating,
        count,
        percentage: Math.round((count / total) * 100)
      };
    });
  }

  /**
   * Calculate average rating
   */
  getAverageRating(): number {
    const reviews = this.reviewsSignal();
    if (reviews.length === 0) return 0;
    
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return Math.round((sum / reviews.length) * 10) / 10;
  }
}